import api from './api';

/**
 * Service for managing hero carousel slides
 *
 * API Endpoints (from CMS Backend):
 * - GET    /api/admin/hero-slides           - Get all slides
 * - GET    /api/admin/hero-slides/page/{page} - Get slides by page
 * - GET    /api/admin/hero-slides/{id}      - Get single slide
 * - PUT    /api/admin/hero-slides/{id}      - Update slide
 * - POST   /api/admin/hero-slides/page/{page}/reorder - Reorder slides
 * - POST   /api/admin/hero-slides/{id}/toggle-active  - Toggle visibility
 *
 * Note: Carousel has exactly 3 fixed slides for home page.
 *       No create/delete operations - edit only.
 */

const API_PATH = '/api/admin/hero-slides';

const heroSlideService = {
  // ============ Read Operations ============

  /**
   * Get all hero slides across all pages
   * @returns {Promise} Axios promise with array of HeroSlide objects
   */
  getAllSlides: () => {
    return api.get(API_PATH);
  },

  /**
   * Get all slides for a specific page (e.g., "home")
   * @param {string} page - Page identifier (default: "home")
   * @returns {Promise} Axios promise with array of HeroSlide objects sorted by displayOrder
   */
  getSlidesByPage: (page = 'home') => {
    return api.get(`${API_PATH}/page/${page}`);
  },

  /**
   * Get all slides for the home page
   * Convenience method for getSlidesByPage('home')
   * @returns {Promise} Axios promise with array of 3 HeroSlide objects
   */
  getHomeSlides: () => {
    return api.get(`${API_PATH}/page/home`);
  },

  /**
   * Get a single slide by ID
   * @param {string} id - Slide UUID
   * @returns {Promise} Axios promise with HeroSlide object
   */
  getSlide: (id) => {
    return api.get(`${API_PATH}/${id}`);
  },

  // ============ Update Operations ============

  /**
   * Update a slide
   * @param {string} id - Slide UUID
   * @param {Object} data - Update data
   * @param {string} data.title - Required, max 255 chars
   * @param {string} [data.subtitle] - Optional
   * @param {string} [data.buttonText] - Optional, max 100 chars
   * @param {string} [data.buttonLink] - Optional, max 255 chars
   * @param {string} [data.buttonStyle] - Optional, CSS class name
   * @param {string} [data.readMoreContent] - Optional, HTML content for modal popup
   * @param {string} data.imageUrl - Required, valid URL
   * @param {boolean} [data.isActive] - Optional
   * @returns {Promise} Axios promise with updated HeroSlide object
   */
  updateSlide: (id, data) => {
    return api.put(`${API_PATH}/${id}`, data);
  },

  // ============ Reorder Operations ============

  /**
   * Reorder slides for a specific page
   * @param {string} page - Page identifier (e.g., "home")
   * @param {string[]} orderedIds - Array of slide UUIDs in desired order
   * @returns {Promise} Axios promise with updated slides array
   */
  reorderSlides: (page, orderedIds) => {
    return api.post(`${API_PATH}/page/${page}/reorder`, { orderedIds });
  },

  /**
   * Reorder slides for the home page
   * Convenience method for reorderSlides('home', orderedIds)
   * @param {string[]} orderedIds - Array of 3 slide UUIDs in desired order
   * @returns {Promise} Axios promise with updated slides array
   */
  reorderHomeSlides: (orderedIds) => {
    return api.post(`${API_PATH}/page/home/reorder`, { orderedIds });
  },

  // ============ Toggle Operations ============

  /**
   * Toggle slide active/inactive status
   * @param {string} id - Slide UUID
   * @returns {Promise} Axios promise with updated HeroSlide object (isActive toggled)
   */
  toggleActive: (id) => {
    return api.post(`${API_PATH}/${id}/toggle-active`);
  },
};

export default heroSlideService;
